import { z } from "zod";
// The file rather than the `../../../stock` barrel: that barrel's `get-new-suborder-state` imports
// back into `../schemas`, and reaching it from a schema would close the cycle at runtime.
import { ORDER_STATES } from "../../../stock/order-states";

/**
 * `projectId` narrows the list to one site. `scope` narrows it to a reach: `organization` is every
 * order the policy admits — the warehouse's own view of the domain — and `ownProjects` is the
 * orders of the sites the caller is staffed on, which is what the personal list asks for. The word
 * is Citadel's and means the same predicate here, but it is not a grant: `Stock` offers
 * organisation reach only, so this is a filter the caller chooses rather than a right the rules
 * confer. Absent, the reach is the organisation.
 *
 * `state` narrows to the lifecycle states asked for, and **absent means every state** — the list
 * has always returned finished and cancelled orders alongside live ones, and a default that
 * dropped them would silently change what four existing callers show. It is a list rather than a
 * single value because the useful questions are plural: the warehouse asks for `pending` and
 * `open` together to see what is owed, and for `complete` and `canceled` together to see what is
 * closed.
 */
export const getOrdersQuerySchema = z.object({
	projectId: z.string().optional(),
	scope: z.enum(["organization", "ownProjects"]).optional(),
	state: z
		.preprocess(
			// A query string carries one `state` as a string and several as an array, so a
			// single-state filter would fail an `z.array` that had not been told the difference.
			(val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
			z.array(z.enum(ORDER_STATES)).optional(),
		)
		.optional(),
});

export type GetOrdersQuerySchema = z.infer<typeof getOrdersQuerySchema>;
